<script setup lang="ts">
import { useStorage } from '@vueuse/core'
import Hero from './@components/Hero/index.vue'
import type { PromptMinified } from '~~/src/types/prompt'
import { useFilters } from '~/composables/filters'
import { getTopic } from '~~/src/server/topics'
import { getHostname } from '~~/src/api'
import type { PromptAdPlaceholder } from '~~/src/types/ad'
import { getElementalHomeFeed } from '~~/src/api/feed'
import type { List } from '~~/src/types/list'
import type { Elemental, ElementalType, ViewType } from '~~/src/types/elemental'
import { ElementalTypeEnum, getSetting } from '~~/src/types/elemental'
import { getElementalTypes } from '~~/src/api/elementals'
import { CONTAINER_MAX_WIDTH } from '@/layouts/default.vue'
import { SEO } from '@/utils/constants'
import { getBreadcrumbsJsonLd } from '~~/src/helpers/serp'
import { useTopicsQuery } from '~~/src/composables/query/topic/useTopicsQuery'

const route = useRoute()
const router = useRouter()
const { firebaseUser } = useFirebaseAuth()
const { data: topics } = useTopicsQuery({
  query: {},
})
const votedPromptsUpdate = useUpdatePromptVote()
const { colors } = useTheme()

const idOrUrl = computed(() => {
  return typeof route.params.id === 'string' ? route.params.id : ''
})
const topic = computed(() => {
  return topics.value?.find(_topic => _topic.id.toString() === idOrUrl.value || _topic.url === idOrUrl.value)
})

const serverTopic = await getTopic(idOrUrl.value)
if (!serverTopic)
  throw showError({ statusCode: 404, message: 'Not found', fatal: true })

const host = getHostname()
const topicUrl = `${host}/${idOrUrl.value}`

const DISTANCE_TO_LOAD_MORE = 3200

const jsonldBreadcrumbs = getBreadcrumbsJsonLd([
  { name: serverTopic?.name, url: topicUrl },
])

useHead({
  link: [
    { rel: 'canonical', href: topicUrl },
  ],
  meta: [
    { name: 'og:title', content: `${serverTopic?.name} | Snack Prompt` },
    { name: 'og:description', content: serverTopic?.description },
    { name: 'og:url', content: topicUrl },
    { name: 'og:image', content: SEO.image },
    { name: 'twitter:title', content: `${serverTopic?.name} | Snack Prompt`.substring(0, 70) },
    { name: 'twitter:description', content: `${serverTopic?.description}`.substring(0, 200) },
    { name: 'twitter:image', content: SEO.image },
    { name: 'twitter:site', content: topicUrl },
  ],
  script: [
    {
      hid: 'breadcrumbs-json-ld',
      type: 'application/ld+json',
      innerHTML: JSON.stringify(jsonldBreadcrumbs),
    },
  ],
})

useSeoMeta({
  title: `${serverTopic?.name} | Snack Prompt`.substring(0, 75),
  description: `${serverTopic?.description}`.substring(0, 170),
  ogTitle: `${serverTopic?.name} | Snack Prompt`,
  ogDescription: serverTopic?.description,
  ogUrl: topicUrl,
  ogImage: SEO.image,
  twitterSite: topicUrl,
  twitterCreator: SEO.twitter,
  twitterTitle: `${serverTopic?.name} | Snack Prompt`.substring(0, 70),
  twitterDescription: `${serverTopic?.description}`.substring(0, 200),
  twitterImage: SEO.image,
})

watch(topic, () => topic.value && !isNaN(Number(idOrUrl.value)) && navigateTo(`/topic/${topic.value.url}`))

if (!idOrUrl.value)
  // TODO: send to 404 page
  router.push('/')

const prompts = ref<(List | PromptMinified | PromptAdPlaceholder)[]>([])
const items = ref<(List | PromptAdPlaceholder | Elemental)[]>([])
const itemsAllTime = ref<(List | PromptMinified | PromptAdPlaceholder | Elemental)[]>([])

const pageSize = 100
const pageNumber = ref(1)
const canLoadMore = ref(true)
const isLoading = ref(true)
const isPendingTopicListRequest = ref(false)

const willShowAllTimePrompts = ref(false)
const pageSizeAllTime = 100
const pageNumberAllTime = ref(1)
const canLoadMoreAllTime = ref(true)
const isLoadingAllTime = ref(false)

const filters = useFilters()
const nextAdPromptIndex = ref(4)

const enterprise = useEnterprise()

const selectedView = ref<ViewType>()
const elementalTypes = ref<ElementalType[]>()
const selectedElementalType = ref<ElementalType>()

const AVAILABLE_TYPES = ['prompt']
const promptTypeStorage = useStorage(PROMPT_TYPE_STORAGE_KEY, PROMPT_TYPE_STORAGE_DEFAULT)

const viewSettings = computed(() => {
  const typeMap: Record<string, string> = {
    promptText: 'TextPrompt',
    promptVisual: 'VisualPrompt',
    // snippet: 'Snippet', // It's not prepared to show snippets yet
    // visualPrompts: 'VisualPrompt',
    // freeImages: 'Image',
    // myImages: 'Image',
  }

  const selectedTab = selectedElementalType.value?.name || 'prompt'
  const typeName = filters.value.type_ids?.includes(PROMPT_TYPES.Visual) ? 'Visual' : 'Text'

  return getSetting(typeMap[`${selectedTab}${typeName}`])
})

const loadTopicItems = async (nextPage = false) => {
  if (!topic.value || !canLoadMore.value || isPendingTopicListRequest.value)
    return

  isLoading.value = true
  isPendingTopicListRequest.value = true

  if (!nextPage) {
    nextAdPromptIndex.value = 4
    pageNumber.value = 1
    items.value = []
  }
  else {
    pageNumber.value++
  }

  const newPage = await getElementalHomeFeed({
    ...filtersToQueries(filters.value),
    topic_id: topic.value.id,
    size: pageSize,
    page: pageNumber.value,
    enterprise_id: enterprise.value,
    is_premium: filters.value.is_premium || undefined,
    elemental_type_id: selectedElementalType.value?.id,
  })

  if (newPage) {
    // let totalListIndex = 0
    const newFeedArray: (List | Elemental | PromptAdPlaceholder)[] = [
      ...(pageNumber.value > 1 ? items.value : []),
    ]

    for (const prompt of newPage) {
      // if (totalListIndex === nextAdPromptIndex.value) {
      //   newFeedArray.push({ isAd: true } as PromptAdPlaceholder)
      //   totalListIndex++
      //   nextAdPromptIndex.value = totalListIndex + 7
      // }

      newFeedArray.push(prompt)
      // totalListIndex++
    }

    items.value = newFeedArray
  }

  if (!newPage || newPage.length < pageSize) {
    canLoadMore.value = false
    if (filters.value.interval !== 0) {
      canLoadMoreAllTime.value = true
      loadAllTimeTopicItems()
    }
  }
  else {
    canLoadMore.value = true
  }

  isLoading.value = false
  isPendingTopicListRequest.value = false

  willShowAllTimePrompts.value = !canLoadMore.value && filters.value.interval !== 0
}

watch(votedPromptsUpdate, () => {
  prompts.value?.forEach((promptV) => {
    const prompt = promptV as PromptMinified
    if (prompt.id === votedPromptsUpdate.value?.id)
      prompt.isVoted = votedPromptsUpdate.value.state === 'added'
  })
})

watchOnce(topics, () => {
  loadTopicItems()
})

watch([filters, idOrUrl, firebaseUser, enterprise], () => {
  isLoading.value = true
  canLoadMore.value = true
  pageNumber.value = 1
  pageNumberAllTime.value = 1

  loadTopicItems()
}, { deep: true })

watch(promptTypeStorage, (newValue, oldValue) => {
  if (newValue !== oldValue && willShowAllTimePrompts.value) {
    canLoadMoreAllTime.value = true
    loadAllTimeTopicItems()
  }
})

watch(firebaseUser, () => {
  if (firebaseUser.value && !canLoadMore.value && willShowAllTimePrompts.value) {
    canLoadMoreAllTime.value = true
    loadAllTimeTopicItems()
  }
})

async function loadAllTimeTopicItems(nextPage = false) {
  if (!topic.value || isLoadingAllTime.value || !canLoadMoreAllTime.value)
    return

  isLoadingAllTime.value = true
  if (nextPage) {
    pageNumberAllTime.value += 1
  }
  else {
    pageNumberAllTime.value = 1
    itemsAllTime.value = []
  }

  if (!['votes', 'newest', 'saves', 'uses'].includes(filters.value.order_by))
    filters.value.order_by = 'votes'

  const result = await getElementalHomeFeed({
    ...filtersToQueries(filters.value),
    topic_id: topic.value?.id,
    size: pageSizeAllTime,
    page: pageNumberAllTime.value,
    enterprise_id: enterprise.value,
    interval: 0,
    is_premium: filters.value.is_premium || undefined,
    // elemental_type_id: selectedElementalType.value?.id,
  }) || []

  const newPromptsArray: (List | Elemental | PromptMinified | PromptAdPlaceholder)[] = [
    ...(itemsAllTime.value || []),
    ...result,
  ]

  itemsAllTime.value = newPromptsArray

  if (result.length < pageSizeAllTime)
    canLoadMoreAllTime.value = false
  isLoadingAllTime.value = false
}

const layoutScrollHandler = () => {
  if (canLoadMore.value && !isLoading.value) {
    loadTopicItems(true)
  }
  else if (canLoadMoreAllTime.value && willShowAllTimePrompts.value && !isLoadingAllTime.value) {
    canLoadMoreAllTime.value = true
    loadAllTimeTopicItems(true)
  }
}

onMounted(async () => {
  if (!['votes', 'newest', 'saves', 'uses'].includes(filters.value.order_by))
    filters.value.order_by = 'votes'

  try {
    if (!elementalTypes.value)
      elementalTypes.value = await getElementalTypes() || []

    selectedElementalType.value = elementalTypes.value?.find(t => t.name === 'prompt')
  }
  catch (_) {}

  if (viewSettings.value)
    selectedView.value = viewSettings.value.defaultView

  if (topics.value?.length && !prompts.value.length)
    loadTopicItems()

  useInfiniteScroll(
    document.querySelector('#main-scroll') as HTMLElement,
    layoutScrollHandler, {
      distance: DISTANCE_TO_LOAD_MORE,
    })
})

watch(viewSettings, () => {
  if (viewSettings.value)
    selectedView.value = viewSettings.value.defaultView
})

const handleLayout = (tab: ViewType) => {
  selectedView.value = tab
}
</script>

<template>
  <NuxtLayout>
    <template #top-content>
      <TopicList />

      <Hero :topic-id-or-name="idOrUrl" />

      <div v-if="selectedView === 'grid'" :style="{ maxWidth: `${CONTAINER_MAX_WIDTH}px` }" class="mx-auto w-full px-2 md:px-4 lg:px-6 py-2.5">
        <PromptFiltersVisual
          :default-layout="selectedView"
          :default-type="selectedElementalType?.name"
          :elemental-types="elementalTypes?.filter(t => AVAILABLE_TYPES.includes(t.name))"
          @change-layout="handleLayout"
        />
      </div>
    </template>

    <template #outer-container>
      <div v-if="selectedView === 'grid'" pb="62px" class="px-2 md:px-4 lg:px-6">
        <ElementalGrid
          v-if="items?.length"
          :elemental-type-id="ElementalTypeEnum.Prompt"
          :data="items as Elemental[]"
          :card-options="viewSettings"
        />
        <div v-else />
        <AppLoading v-if="isLoading" />
        <Text v-else-if="!items?.length" variant="small" text-bluish-softer text-center w-full my-4>
          No items found
        </Text>

        <div
          v-if="willShowAllTimePrompts && filters.interval !== 0"
          :style="{ maxWidth: `${CONTAINER_MAX_WIDTH}px` }" mx-auto p-0 pb-10
        >
          <div w-full flex items-center gap-5 py-5 md:px-2>
            <div flex-grow h="1px" bg-soft-gray />
            <Text variant="small" :color="colors.bluishSofter">
              Here are the best all-time {{ filters.item_type === 'prompt' ? 'prompts' : (filters.item_type === 'list' ? 'lists' : 'prompts and lists') }}
            </Text>
            <div flex-grow h="1px" bg-soft-gray />
          </div>

          <ElementalGrid
            v-if="itemsAllTime?.length" :data="itemsAllTime as Elemental[]"
            :elemental-type-id="ElementalTypeEnum.Prompt"
            :card-options="viewSettings"
          />

          <AppLoading v-if="isLoadingAllTime" />
        </div>
      </div>

      <div
        v-else
        relative flex w-full mx-auto px-2 pt-5 pb="62px" :style="{ maxWidth: `${CONTAINER_MAX_WIDTH}px` }"
      >
        <main flex="~ col" flex-1 mx-auto w-full pb-16>
          <ClientOnly>
            <div pb-5>
              <PromptFiltersVisual
                :default-layout="selectedView"
                :default-type="selectedElementalType?.name"
                :elemental-types="elementalTypes?.filter(t => AVAILABLE_TYPES.includes(t.name))"
                @change-layout="handleLayout"
              />
            </div>
          </ClientOnly>

          <div>
            <Feed
              :items="items" :is-loading="isLoading" :can-load-more="canLoadMore" :show-empty-feed-message="true"
              @load-more="() => loadTopicItems(true)"
            />
          </div>

          <div v-if="willShowAllTimePrompts && filters.interval !== 0">
            <div w-full flex items-center gap-5 py-5>
              <div flex-grow h="1px" bg-soft-gray />
              <Text variant="small" :color="colors.bluishSofter">
                Here are the best all-time {{ filters.item_type === 'prompt' ? 'prompts' : (filters.item_type === 'list' ? 'lists' : 'prompts and lists') }}
              </Text>
              <div flex-grow h="1px" bg-soft-gray />
            </div>

            <Feed
              :items="itemsAllTime" :is-loading="isLoadingAllTime" :can-load-more="canLoadMoreAllTime" :show-empty-feed-message="true"
              @load-more="() => loadAllTimeTopicItems(true)"
            />
          </div>
        </main>
      </div>
    </template>
  </NuxtLayout>
</template>
